import { createSelector } from '@ngrx/store';
import { SurveyMarginEntryInspector } from '@ps/survey-utils';
import { surveyCreationFeature } from '../survey-creation.feature';
import { DEFAULT_CHILD_AUDIENCE_STATE } from '../survey-creation-state.constants';
import { resolveActiveChildKey } from './utils/audience-children.helper';
import { selectSurvey } from '../core/survey-creation-core.selectors';

const { selectAudience, selectCore } = surveyCreationFeature;

/**
 * The child currently being worked on — see `resolveActiveChildKey`. Falls
 * back to a fresh default child so downstream selectors never have to
 * null-check a missing entry.
 */
const selectActiveChild = createSelector(
  selectAudience,
  selectCore,
  (state, core) => {
    const key = resolveActiveChildKey(state, core.activeChildKey);
    return state.children[key] ?? { ...DEFAULT_CHILD_AUDIENCE_STATE, key };
  },
);

export const selectMetadataForm = createSelector(
  selectActiveChild,
  ({ metadata }) => metadata,
);

export const selectSurveyClickBalancing = createSelector(
  selectMetadataForm,
  ({ value }) => !!value?.clickBalancing,
);

export const selectIsMetadataValid = createSelector(
  selectMetadataForm,
  ({ valid }) => valid,
);

// The live completes (goal) being edited in the audience form — distinct from
// the saved `survey.completesRequired`, which only changes on save.
export const selectMetadataCompletes = createSelector(
  selectMetadataForm,
  ({ value }) => value?.completes ?? null,
);

// Live LOI/IR being edited in the audience form — distinct from the saved
// `survey.expectedLoi`/`survey.expectedIr`, which only change on save.
export const selectMetadataLoi = createSelector(
  selectMetadataForm,
  ({ value }) => value?.loi ?? null,
);

export const selectMetadataIncidence = createSelector(
  selectMetadataForm,
  ({ value }) => value?.incidence ?? null,
);

export const selectMetadataFieldTime = createSelector(
  selectMetadataForm,
  ({ value }) => value?.fieldTime ?? null,
);

export const selectQualificationsForm = createSelector(
  selectActiveChild,
  ({ qualifications }) => qualifications,
);

export const selectQuotasForm = createSelector(
  selectActiveChild,
  ({ quotas }) => quotas,
);

export const selectSurveyFormState = createSelector(
  selectMetadataForm,
  selectQualificationsForm,
  selectQuotasForm,
  (metadata, qualifications, quotas) => ({ metadata, qualifications, quotas }),
);

/** The applied Feasibility filters (exclusions/inclusions) */
export const selectAdvancedFeasibilityFilters = createSelector(
  selectActiveChild,
  ({ advancedFeasibilityFilters }) => advancedFeasibilityFilters,
);

export const selectLocationFileUpload = createSelector(
  selectActiveChild,
  ({ locationFileUpload }) => locationFileUpload,
);

export const selectLinkedTagIds = createSelector(
  selectActiveChild,
  ({ tagLinks }) => tagLinks.linkedTagIds,
);

export const selectLoadingLinkedTags = createSelector(
  selectActiveChild,
  ({ tagLinks }) => tagLinks.loadingLinkedTags,
);

export const selectSelectedTagIds = createSelector(
  selectActiveChild,
  ({ tagLinks }) => tagLinks.selectedTagIds,
);

export const selectLinkTagsError = createSelector(
  selectActiveChild,
  ({ tagLinks }) => tagLinks.linkTagsError,
);

export const selectSuppliersMetaDataBlendId = createSelector(
  selectActiveChild,
  ({ suppliersMetaData }) => suppliersMetaData.blendId,
);

export const selectSuppliersMetaData = createSelector(
  selectActiveChild,
  ({ suppliersMetaData }) => suppliersMetaData,
);

export const selectIsDirty = createSelector(
  selectActiveChild,
  ({ metadata, margin, qualifications, quotas }) =>
    metadata.isDirty ||
    margin.isDirty ||
    qualifications.isDirty ||
    quotas.isDirty,
);

export const selectMarginForm = createSelector(
  selectActiveChild,
  ({ margin }) => margin,
);

export const selectPendingHighCpiConfirmations = createSelector(
  selectActiveChild,
  ({ pendingHighCpiConfirmations }) => pendingHighCpiConfirmations,
);

/**
 * Whether the Target Margin field may be written. Before the draft exists there
 * is no survey to inspect, so the field stays editable.
 */
export const selectCanUpdateTargetMargin = createSelector(
  selectSurvey,
  (survey) =>
    !survey ||
    new SurveyMarginEntryInspector({
      isManualScpiOverrideEnabled: survey.isManualScpiOverrideEnabled,
      isMarginMaxEnabled: survey.isMarginMaxEnabled,
      status: survey.status,
    }).isEntryAllowed(),
);

export const selectIsPreDraft = createSelector(
  selectCore,
  selectAudience,
  (core, state) => !core.rootSurveyId && Object.keys(state.children).length > 1,
);

export const selectAudienceChildren = createSelector(
  selectAudience,
  ({ children }) => children,
);

export const selectHasChildSurveys = createSelector(
  selectCore,
  selectAudience,
  (core, { children }) =>
    !!core.rootSurveyId && Object.keys(children).length > 1,
);

export const selectIsDrawablePreDraft = createSelector(
  selectIsMetadataValid,
  selectIsPreDraft,
  (isMetadataValid, isPreDraft) => isMetadataValid && isPreDraft,
);
